import { ANGLE_GUIDE, DEFAULT_POSTOP_STEPS, PHOTO_ANGLES, type PostopSettings, type PostopStep } from '@dnx/contracts';
import { Alert, Badge, Button, Card, Field, Input, Spinner, cn } from '@dnx/ui';
import { queryOptions, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { meQuery } from '../lib/session.js';
import { SectionHeader } from './layout.js';

const settingsQuery = queryOptions({ queryKey: ['postop', 'settings'], queryFn: () => api<PostopSettings>('/postop/settings') });
const area = 'w-full resize-y rounded-md border border-border-strong bg-surface px-3 py-2 text-sm text-fg focus:border-primary disabled:opacity-70';

function StepEditor({ step, onChange, onRemove, disabled }: { step: PostopStep; onChange: (s: PostopStep) => void; onRemove: () => void; disabled: boolean }) {
  const toggle = (a: PostopStep['photoAngles'][number]) => onChange({ ...step, photoAngles: step.photoAngles.includes(a) ? step.photoAngles.filter((x) => x !== a) : [...step.photoAngles, a] });
  return (
    <Card className="grid gap-3 p-4">
      <div className="grid gap-3 sm:grid-cols-[110px_minmax(0,1fr)_auto] sm:items-end">
        <Field label="Dia (D+)"><Input type="number" min={0} max={730} value={step.day} disabled={disabled} onChange={(e) => onChange({ ...step, day: Number(e.target.value) })} /></Field>
        <Field label="Título"><Input value={step.title} disabled={disabled} maxLength={80} onChange={(e) => onChange({ ...step, title: e.target.value })} /></Field>
        {!disabled && <Button variant="ghost" size="icon" aria-label={`Remover dia ${step.day}`} onClick={onRemove}><Trash2 className="size-4" /></Button>}
      </div>
      <label className="grid gap-1.5 text-[13px] font-medium text-fg">Mensagem no WhatsApp <span className="font-normal text-fg-muted">(vazia = não manda; use {'{nome}'})</span>
        <textarea className={area} rows={2} maxLength={1000} disabled={disabled} value={step.message} onChange={(e) => onChange({ ...step, message: e.target.value })} />
      </label>
      <label className="grid gap-1.5 text-[13px] font-medium text-fg">Orientação do dia na área do paciente
        <textarea className={area} rows={2} maxLength={2000} disabled={disabled} value={step.instructions} onChange={(e) => onChange({ ...step, instructions: e.target.value })} />
      </label>
      <div>
        <p className="text-[13px] font-medium text-fg">Pedir fotos</p>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {PHOTO_ANGLES.map((a) => (
            <button key={a} type="button" disabled={disabled} aria-pressed={step.photoAngles.includes(a)} onClick={() => toggle(a)}
              className={cn('rounded-full border px-2.5 py-1 text-[12px]', step.photoAngles.includes(a) ? 'border-primary bg-primary-soft text-primary' : 'border-border text-fg-muted hover:border-border-strong')}>
              {ANGLE_GUIDE[a].label}
            </button>
          ))}
        </div>
      </div>
    </Card>
  );
}

/** Configurações › Pós-operatório: telefone do plantão e o protocolo D+1…D+365 da clínica. */
export function PostopSettingsPage() {
  const qc = useQueryClient();
  const { data: me } = useSuspenseQuery(meQuery);
  const canEdit = me.permissions.includes('documents.template.manage');
  const { data } = useQuery(settingsQuery);
  const [phone, setPhone] = useState('');
  const [steps, setSteps] = useState<PostopStep[]>([]);
  useEffect(() => { if (data) { setPhone(data.onCallPhone ?? ''); setSteps(data.steps); } }, [data]);
  const save = useMutation({
    mutationFn: () => api<PostopSettings>('/postop/settings', { method: 'PUT', body: { onCallPhone: phone.trim() || null, steps: [...steps].sort((a, b) => a.day - b.day) } }),
    onSuccess: (n) => qc.setQueryData(settingsQuery.queryKey, n),
  });
  if (!data) return <Spinner className="size-5 text-fg-subtle" label="Carregando" />;
  const set = (i: number, s: PostopStep) => setSteps(steps.map((x, j) => (j === i ? s : x)));
  const next = Math.max(0, ...steps.map((s) => s.day)) + 30;
  return (
    <div className="grid gap-6">
      <SectionHeader title="Pós-operatório" description="O plano de cada paciente copia este protocolo quando a ficha cirúrgica é assinada; mudanças valem para as próximas cirurgias."
        actions={data.isDefault ? <Badge>Protocolo padrão</Badge> : <Badge tone="primary">Protocolo da clínica</Badge>} />
      <Card className="grid gap-4 p-5 sm:grid-cols-[minmax(0,320px)_1fr] sm:items-end">
        <Field label="Telefone do plantão"><Input value={phone} disabled={!canEdit} maxLength={30} placeholder="(11) 99999-0000" onChange={(e) => setPhone(e.target.value)} /></Field>
        <p className="text-[13px] text-fg-muted">Aparece para o paciente quando ele relata um sintoma urgente, no portal e na resposta automática do WhatsApp.</p>
      </Card>
      <div className="grid gap-3">
        {steps.map((s, i) => <StepEditor key={i} step={s} disabled={!canEdit} onChange={(n) => set(i, n)} onRemove={() => setSteps(steps.filter((_, j) => j !== i))} />)}
      </div>
      {save.error && <Alert tone="danger">{save.error.message}</Alert>}
      {canEdit && (
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => setSteps([...steps, { day: next, title: `Dia ${next}`, message: '', instructions: '', photoAngles: [] }])}><Plus className="size-4" />Adicionar dia</Button>
          <Button variant="ghost" onClick={() => setSteps(DEFAULT_POSTOP_STEPS)}>Restaurar padrão</Button>
          <Button className="ml-auto" onClick={() => save.mutate()} loading={save.isPending}>Salvar</Button>
        </div>
      )}
    </div>
  );
}
